import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, LogOut, AlertCircle } from 'lucide-react';
import toast from 'react-hot-toast';
import DemoPostcardBuilder from '../components/DemoPostcardBuilder';
import DemoEventPlanner from '../components/DemoEventPlanner';
import DemoJGATemplateSelector from '../components/DemoJGATemplateSelector';

export default function DemoPage() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const [packageData, setPackageData] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionValid, setSessionValid] = useState(true);

  // Fetch package and validate session
  useEffect(() => {
    const validateSession = async () => {
      const sessionToken = localStorage.getItem('demoSessionToken');
      const storedPackageId = localStorage.getItem('demoPackage');
      const startTime = parseInt(localStorage.getItem('demoStartTime'));

      if (!sessionToken || !storedPackageId || !startTime) {
        navigate('/');
        toast.error('Demo-Session ungültig');
        return;
      }

      // Fetch package data
      try {
        const res = await fetch(`/api/packages/${storedPackageId}`);
        const data = await res.json();
        
        if (data.status === 'success') {
          setPackageData(data.package);
        }
      } catch (err) {
        console.error('Failed to fetch package:', err);
      }

      setLoading(false);
    };

    validateSession();
  }, [packageId, navigate]);

  // Timer for 30 minutes
  useEffect(() => {
    const updateTimer = () => {
      const startTime = parseInt(localStorage.getItem('demoStartTime'));
      if (!startTime) return;

      const elapsedMs = Date.now() - startTime;
      const totalMs = 30 * 60 * 1000; // 30 minutes
      const remainingMs = totalMs - elapsedMs;

      if (remainingMs <= 0) {
        setSessionValid(false);
        setTimeRemaining('Abgelaufen');
      } else {
        const minutes = Math.floor(remainingMs / 60000);
        const seconds = Math.floor((remainingMs % 60000) / 1000);
        setTimeRemaining(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      }
    };

    updateTimer(); // Initial call
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, []);

  const handleEndDemo = () => {
    localStorage.removeItem('demoSessionToken');
    localStorage.removeItem('demoPackage');
    localStorage.removeItem('demoStartTime');
    navigate('/');
    toast.success('Demo beendet');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-4xl mb-4">⏳</div>
          <p>Demo wird geladen...</p>
        </div>
      </div>
    );
  }

  if (!sessionValid) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center max-w-md">
          <AlertCircle size={48} className="mx-auto mb-4 text-red-500" />
          <h2 className="text-2xl font-bold mb-2">Demo-Session abgelaufen</h2>
          <p className="text-gray-600 mb-6">
            Deine 30-Minuten Demo ist vorbei. Bereit, das echte Paket zu kaufen?
          </p>
          <button
            onClick={() => navigate('/checkout?package=' + packageId)}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition font-bold"
          >
            Jetzt kaufen
          </button>
        </div>
      </div>
    );
  }

  if (!packageData) {
    return <div>Paket konnte nicht geladen werden</div>;
  }

  const renderDemoContent = () => {
    switch (packageId) {
      case 'postcard':
        return <DemoPostcardBuilder />;
      case 'family':
      case 'family-plus':
      case 'jga':
      case 'jga-plus':
        return packageId === 'jga-plus' ? (
          <DemoJGATemplateSelector />
        ) : (
          <DemoEventPlanner packageData={packageData} />
        );
      default:
        return <DemoEventPlanner packageData={packageData} />;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header with Timer */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">{packageData.icon} {packageData.name} Demo</h1>
              <p className="text-gray-600">Kostenlos für 30 Minuten testen</p>
            </div>

            {/* Timer & End Button */}
            <div className="flex items-center gap-4">
              {/* Timer */}
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 1, repeat: Infinity }}
                className="flex items-center gap-2 bg-orange-100 text-orange-700 px-4 py-2 rounded-lg font-bold"
              >
                <Clock size={20} />
                <span>{timeRemaining}</span>
              </motion.div>

              {/* End Demo Button */}
              <button
                onClick={handleEndDemo}
                className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition flex items-center gap-2"
              >
                <LogOut size={18} /> Demo beenden
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info Banner */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8 flex items-start gap-3"
        >
          <AlertCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
          <div>
            <p className="text-blue-900 font-bold mb-1">Demo-Modus</p>
            <p className="text-blue-800 text-sm">
              Dies ist eine kostenlose Demo. Die Daten werden nach der Demo nicht gespeichert.
              Upgrade auf ein bezahltes Paket, um deine Events und Nachrichten dauerhaft zu speichern.
            </p>
          </div>
        </motion.div>

        {/* Demo Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          {renderDemoContent()}
        </motion.div>

        {/* Package Features */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-12 bg-white rounded-lg p-8 shadow-lg"
        >
          <h2 className="text-2xl font-bold mb-6">📋 Paket-Features</h2>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-4 rounded-lg">
              <p className="text-gray-600 text-sm">Empfänger</p>
              <p className="text-3xl font-bold text-purple-600">
                {packageData.limits.maxRecipients === null ? '∞' : packageData.limits.maxRecipients}
              </p>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
              <p className="text-gray-600 text-sm">Events</p>
              <p className="text-3xl font-bold text-blue-600">
                {packageData.limits.maxEvents === null ? '∞' : packageData.limits.maxEvents}
              </p>
            </div>
            <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
              <p className="text-gray-600 text-sm">Messages</p>
              <p className="text-3xl font-bold text-green-600">
                {packageData.limits.maxMessages === null ? '∞' : packageData.limits.maxMessages}
              </p>
            </div>
            <div className="bg-gradient-to-br from-pink-50 to-pink-100 p-4 rounded-lg">
              <p className="text-gray-600 text-sm">Preis</p>
              <p className="text-3xl font-bold text-pink-600">EUR {packageData.price}</p>
            </div>
          </div>

          {/* Features List */}
          <h3 className="font-bold text-lg mb-3">Verfügbare Features:</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {packageData.features.map((feature, idx) => (
              <div key={idx} className="flex items-center gap-2 text-gray-700">
                <span className="text-green-500">✓</span>
                <span>{feature}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-8 text-center"
        >
          <button
            onClick={() => navigate('/checkout?package=' + packageId)}
            className="px-8 py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition font-bold text-lg"
          >
            Jetzt fuer EUR {packageData.price} kaufen
          </button>
          <p className="text-gray-600 mt-3">
            Inklusive 14 Tage Geld-zurück-Garantie
          </p>
        </motion.div>
      </div>
    </div>
  );
}
